import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import CaleoSecondaryButton from 'Components/reusable/Buttons/CaleoSecondaryButton'
import React from 'react'
import { useTranslation } from 'react-i18next'

/** An existing option offered instead of creating a new one. */
export interface DidYouMeanSuggestion {
  id: number
  /** Name in the current language. */
  label: string
  /** Other names of the option (e.g. other languages), shown when they differ from the label. */
  otherNames: string[]
  /** Already added, so it can't be picked. */
  disabled: boolean
}

interface DidYouMeanDialogProps {
  open: boolean
  /** The name the user is about to create. */
  name: string
  suggestions: DidYouMeanSuggestion[]
  onPick: (id: number) => void
  onCreate: () => void
  onCancel: () => void
}

/**
 * Asks "Did you mean …?" before a new skill, certificate or competence is created, listing existing
 * options with a similar name. The user picks one of them, creates the new option anyway, or cancels.
 *
 * @returns The dialog.
 * @notExported
 */
const DidYouMeanDialog: React.FC<DidYouMeanDialogProps> = ({ open, name, suggestions, onPick, onCreate, onCancel }) => {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth aria-labelledby="did-you-mean-dialog-title">
      <DialogTitle id="did-you-mean-dialog-title">{t('didYouMean.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{t('didYouMean.description', { name })}</Typography>
        <List dense>
          {suggestions.map(suggestion => (
            <ListItemButton
              key={suggestion.id}
              data-testid={`did-you-mean-option-${suggestion.id}`}
              disabled={suggestion.disabled}
              onClick={() => onPick(suggestion.id)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}
            >
              {/* The theme colors list item texts for the dark navigation menu */}
              <ListItemText
                sx={{ color: 'text.primary' }}
                primary={suggestion.label}
                secondary={[
                  ...suggestion.otherNames.filter(otherName => otherName !== suggestion.label),
                  ...(suggestion.disabled ? [t('didYouMean.alreadyAdded')] : []),
                ].join(' · ')}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ paddingRight: '22px', paddingBottom: '22px' }}>
        <CaleoSecondaryButton testId="did-you-mean-cancel" label={t('cancel')} valid clickAction={onCancel} />
        <CaleoPrimaryButton
          testId="did-you-mean-create"
          label={t('didYouMean.create', { name })}
          valid
          clickAction={onCreate}
        />
      </DialogActions>
    </Dialog>
  )
}

export default DidYouMeanDialog
